import React, { useMemo } from 'react';

import { Button, Center, Group, Loader, Table, px, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import PayRateSelect from 'components/form-fields/select-fields/PayRateSelect';
import useClassCoachRoles from 'hooks/classCoachRole/useClassCoachRoles';
import useClassPayRateLookups from 'hooks/classPayRateLookup/useClassPayRateLookups';
import useUpdateClassPayRateLookup from 'hooks/classPayRateLookup/useUpdateClassPayRateLookup';
import useClassTypes from 'hooks/classType/useClassTypes';
import useAllPages from 'hooks/useAllPages';
import { KiloClassPayRateLookup } from 'types';
import { getClassTypeRoleToPayRateLookup } from 'utils/payRates.helpers';

import ClassCoachRoleTableHeader from './components/ClassCoachRoleTableHeader';
import ModalCreateClassCoachRole from './components/ModalCreateClassCoachRole';
import useAssignPayRatesTableStyles from '../AppointmentPayRateLookupTable/components/styles';

interface IClassPayRateLookupTableProps {
  permission?: boolean;
  staff: string;
}

const ClassPayRateLookupTable = ({
  staff,
  permission = true,
}: IClassPayRateLookupTableProps): JSX.Element => {
  const theme = useMantineTheme();
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const { classes } = useAssignPayRatesTableStyles();
  const updateMutation = useUpdateClassPayRateLookup();

  const {
    items: lookups,
    isLoading: payRateLookupsLoading,
    isSuccess: payRateLookupsSuccess,
  } = useAllPages(useClassPayRateLookups({ staff, limit: '100' }));
  const {
    items: coachRoles,
    isLoading: coachRolesLoading,
    isSuccess: coachRolesSuccess,
  } = useAllPages(useClassCoachRoles({ limit: '100' }));
  const {
    items: classTypes,
    isLoading: classTypesLoading,
    isSuccess: classTypesSuccess,
  } = useAllPages(useClassTypes({ limit: '100' }));

  const classTypeRoleToPayRateLookup = useMemo(() => getClassTypeRoleToPayRateLookup(lookups), [lookups]);

  const handleChangePayRate = (
    payRateId: string | null,
    values: KiloClassPayRateLookup | undefined,
  ): void => {
    if (!!values) {
      const payload = {
        ...values,
        payRate: payRateId,
      };
      updateMutation.mutate(payload);
    }
  };

  return (
    <>
      {(payRateLookupsLoading || coachRolesLoading || classTypesLoading) && (
        <Center my='xl'>
          <Loader />
        </Center>
      )}
      {payRateLookupsSuccess && coachRolesSuccess && classTypesSuccess && (
        <Group align='top' className={classes.table}>
          <div style={{ overflowX: 'auto', maxWidth: '100%', width: '100%' }}>
            <Table withColumnBorders withBorder verticalSpacing='md' sx={{ minWidth: 'max-content' }}>
              <thead>
                <tr>
                  <th style={{ width: `${px(theme.spacing.xl) * 10}px`, background: theme.colors.gray[3] }}>
                    Class Rates
                  </th>
                  {coachRoles.map((role, i) => (
                    <ClassCoachRoleTableHeader key={i} role={role} permission={permission} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {classTypes.map((classType, i) => (
                  <tr key={classType.kiloClassTypeId}>
                    <td style={{ width: `${px(theme.spacing.xl) * 10}px` }}>{classType.name}</td>
                    {coachRoles.map((role, y) => (
                      <td
                        key={`${classType.kiloClassTypeId}-${role.kiloClassCoachRoleId}`}
                        style={{ width: `${px(theme.spacing.xl) * 10}px` }}
                      >
                        <PayRateSelect
                          cyData={`classPayRateSelect${i}${y}`}
                          disabled={!permission}
                          value={
                            classTypeRoleToPayRateLookup[
                              `${classType.kiloClassTypeId}-${role.kiloClassCoachRoleId}`
                            ]?.payRate
                          }
                          onChange={event =>
                            handleChangePayRate(
                              event,
                              classTypeRoleToPayRateLookup[
                                `${classType.kiloClassTypeId}-${role.kiloClassCoachRoleId}`
                              ],
                            )
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <Button
            leftIcon={<IconPlus size={theme.fontSizes.sm} />}
            onClick={openCreateModal}
            disabled={!permission}
            data-cy='createClassCoachRoleButton'
          >
            Create Coach Role For Class
          </Button>
        </Group>
      )}
      <ModalCreateClassCoachRole opened={createModalOpened} handleClose={closeCreateModal} />
    </>
  );
};

export default ClassPayRateLookupTable;
